import { type PortfolioDetail as PortfolioDetailResponse, type PortfolioKpi } from '@d3/contract';

import {
  type PortfolioDetail,
  type PortfolioKpi as PortfolioKpiVM,
  type PortfolioManagerContact,
  SECTOR_OPTIONS,
  type SectorOption,
} from '../model/portfolio';

/** 계약 서버는 섹터를 문자열로 준다. 모르는 값 · None · 빈 값 = 선택 안 함(null) */
function toSector(value: string | null): SectorOption | null {
  return value && (SECTOR_OPTIONS as readonly string[]).includes(value)
    ? (value as SectorOption)
    : null;
}

/** KPI 줄 순서: 비즈니스(isMajor) 먼저, 같은 종류는 id 순(레거시 PortfolioDetailHook 의 KPISort) */
export function sortKpis<T extends Pick<PortfolioKpi, 'id' | 'isMajor'>>(kpis: readonly T[]): T[] {
  return [...kpis].sort((a, b) => (a.isMajor === b.isMajor ? a.id - b.id : a.isMajor ? -1 : 1));
}

/**
 * 계약의 중첩 managers(dealsourcing · diligence · manageVics · internal · internalManage) → 뷰모델의 평면 담당 심사역 배열.
 * 포트폴리오 사용자는 managers=null → 빈 배열. 내부 담당(internal · internalManage)은 LP 에게 빈 목록으로 온다.
 */
function toManagerContacts(
  managers: PortfolioDetailResponse['managers'],
): PortfolioManagerContact[] {
  if (!managers) return [];
  const many = (
    refs: readonly { id: number; name: string | null }[],
    type: PortfolioManagerContact['type'],
  ): PortfolioManagerContact[] =>
    refs.map((ref) => ({ memberIdx: ref.id, type, name: ref.name ?? '', mobile: null, email: '' }));
  const one = (
    ref: { id: number; name: string | null } | null,
    type: PortfolioManagerContact['type'],
  ): PortfolioManagerContact[] =>
    ref ? [{ memberIdx: ref.id, type, name: ref.name ?? '', mobile: null, email: '' }] : [];
  return [
    ...one(managers.dealsourcing, 'Dealsourcing'),
    ...one(managers.diligence, 'Diligence'),
    ...one(managers.manageVics, 'ManageVics'),
    ...many(managers.internal, 'ManageInternal'),
    // 구 서버(비교 점검)처럼 internalManage 가 없는 응답도 받는다
    ...many(managers.internalManage ?? [], 'Internal'),
  ];
}

/**
 * 응답(@d3/contract PortfolioDetail) → 상세 뷰모델. 계약 재구성을 흡수한다:
 * id→idx · members→member · kpis→kpi · managers(중첩)→manager(평면) · graphComments(중첩)→commentGraph* ·
 * commentImpactUnsdgs→commentImpactUNDGs · sector 평면. 지표(status · valuation · irr · multiple)는
 * 계약의 `indicators`(nullable)에서 당겨온다 — 투자목록/지표 분리는 2단계.
 * 투자 목록은 entities/investment 가 따로 고른다.
 */
export function toPortfolioDetail(dto: PortfolioDetailResponse): PortfolioDetail {
  const i = dto.indicators;
  const g = dto.graphComments;
  return {
    idx: dto.id,
    name: dto.name,
    username: dto.username ?? '',
    address: dto.address ?? '',
    businessNumber: dto.businessNumber ?? '',
    corporationNumber: dto.corporationNumber ?? '',
    companySector: toSector(dto.sector),
    companyLogo: dto.logo,
    homepageUrl: dto.homepageUrl,
    establishedAt: dto.establishedAt,
    ceoName: dto.ceoName,
    ceoMobile: dto.ceoMobile,
    ceoEmail: dto.ceoEmail,
    member: dto.members.map((member) => ({
      idx: member.id,
      name: member.name ?? '',
      email: member.email ?? '',
      mobile: member.mobile ?? '',
    })),
    manager: toManagerContacts(dto.managers),
    externalManagers: dto.externalManagers.map(({ company, name, mobile, email }) => ({
      company,
      name,
      mobile,
      email,
    })),
    commentInvestmentHighlight: dto.commentInvestmentHighlight,
    commentInvestmentBusiness: dto.commentInvestmentBusiness,
    commentImpactUNDGs: dto.commentImpactUnsdgs,
    commentImpactProblem: dto.commentImpactProblem,
    commentImpactSolution: dto.commentImpactSolution,
    commentGraphRevenue: g?.revenue ?? null,
    commentGraphEquivalent: g?.equivalent ?? null,
    commentGraphEmployee: g?.employee ?? null,
    commentGraphMajorKPI: g?.majorKpi ?? null,
    commentGraphMinorKPI: g?.minorKpi ?? null,
    kpi: sortKpis(dto.kpis).map((kpi): PortfolioKpiVM => ({
      idx: kpi.id,
      title: kpi.title ?? '',
      comment: kpi.comment ?? null,
      isMajor: kpi.isMajor,
    })),
    isUsingConnect: dto.isUsingConnect,
    status: i?.status ?? 'None',
    defaultCurrency: dto.defaultCurrency === 'USD' ? 'USD' : 'KRW',
    totalValuation: i?.totalValuation ?? 0,
    totalValuationExchangeRate: 1,
    remainValuation: i?.remainValuation.amount ?? 0,
    remainValuationExchangeRate: i?.remainValuation.exchangeRate ?? 1,
    valuatedAt: i?.valuatedAt ?? null,
    irr: i?.irr ?? null,
    multiple: i?.multiple ?? null,
  };
}
